import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Only English is bundled. Every other locale is fetched on demand by the
// backend below, which is what keeps them out of main.js — all 25 locales used
// to be imported statically here, which put ~5.2 MB of translations nobody was
// reading into the initial download.
import enHomepage from "./en/homepage.json";
import enHeader from "./en/header.json";
import enVision from "./en/vision.json";
import enEcosystem from "./en/ecosystem.json";
import enContactUs from "./en/contact.json";
import enAboutMst from "./en/about.json";
import enValidationProcess from "./en/blockvalidation.json";
import enDaoAndMstChain from "./en/daoandmstchain.json";
import enFractionalNode from "./en/fractionalnode.json";
import enEvmBlockChain from "./en/evmblockchain.json";
import enAmbassador from "./en/ambassador.json";
import enTransparency from "./en/transparency.json";
import enSolutionsSlug from "./en/solutions.json";
import enBuildOnMst from "./en/buildonmst.json";
import enPrivacyPolicy from "./en/privacy.json";
import enTermsAndCondition from "./en/terms.json";
import enCancellation from "./en/cancellation.json";
import enKycPolicy from "./en/kycpolicy.json";
import enAmlPolicy from "./en/amlpolicy.json";
import enTeamAndPartner from "./en/teampartner.json";
import enCommunity from "./en/community.json";
import enMstResearchProgram from "./en/mstresearch.json";
import enFooter from "./en/footer.json";
import enNotFound from "./en/notfound.json";
import enUseCaseDetails from "./en/usecase_details.json";
import enLearnDetails from "./en/learn_details.json";
import enCommon from "./en/common.json";
import enValidator from "./en/validator.json";
import enPartners from "./en/partners.json";

export const namespaces = [
  "homepage",
  "header",
  "vision",
  "ecosystem",
  "contact",
  "about",
  "blockvalidation",
  "daoandmstchain",
  "fractionalnode",
  "evmblockchain",
  "ambassador",
  "transparency",
  "solutions",
  "buildonmst",
  "privacy",
  "terms",
  "cancellation",
  "kycpolicy",
  "amlpolicy",
  "teampartner",
  "community",
  "mstresearch",
  "footer",
  "notfound",
  "partners",
  "usecase_details",
  "learn_details",
  "common",
  "validator",
];

// i18next backend contract: read(language, namespace, callback).
// The template literal makes webpack emit one lazily-loaded chunk per JSON file
// instead of inlining them; English is excluded because it ships in `resources`.
const lazyLocaleBackend = {
  type: "backend",
  init() {},
  read(language, namespace, callback) {
    import(
      /* webpackChunkName: "locale-[request]" */
      /* webpackExclude: /^\.\/en\// */
      `./${language}/${namespace}.json`
    )
      .then((mod) => callback(null, mod.default || mod))
      // `false` tells i18next not to retry: some locales genuinely lack a few
      // namespaces, and those keys should just fall back to English.
      .catch(() => callback(null, false));
  }
};

i18n
  .use(lazyLocaleBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "en",
    supportedLngs: false,
    ns: namespaces,
    // English is already here, so i18next must not assume a language is missing
    // just because the rest of its namespaces have not arrived yet.
    partialBundledLanguages: true,
    detection: {
      order: ["localStorage", "cookie", "navigator"],
      caches: ["localStorage"],
    },
    resources: {
      en: {
      homepage: enHomepage,
      header: enHeader,
      vision: enVision,
      ecosystem: enEcosystem,
      contact: enContactUs,
      about: enAboutMst,
      blockvalidation: enValidationProcess,
      daoandmstchain: enDaoAndMstChain,
      fractionalnode: enFractionalNode,
      evmblockchain: enEvmBlockChain,
      ambassador: enAmbassador,
      transparency: enTransparency,
      solutions: enSolutionsSlug,
      buildonmst: enBuildOnMst,
      privacy: enPrivacyPolicy,
      terms: enTermsAndCondition,
      cancellation: enCancellation,
      kycpolicy: enKycPolicy,
      amlpolicy: enAmlPolicy,
      teampartner: enTeamAndPartner,
      community: enCommunity,
      mstresearch: enMstResearchProgram,
      footer: enFooter,
      notfound: enNotFound,
      partners: enPartners,
      usecase_details: enUseCaseDetails,
      learn_details: enLearnDetails,
      common: enCommon,
      validator: enValidator,
      },
    },
    react: {
      // No Suspense boundary wraps these components. Without this a language
      // switch would throw instead of rendering the English fallback while the
      // chunk loads.
      useSuspense: false,
    },
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;
